import { apiV2, errorHandler } from "./index";
import { API_ERROR } from "../constants/ActionTypes";
import Swal from "sweetalert2";

/**
 * Query BigQuery research data with filters and pagination.
 */
export function queryResearchData(filters = {}, page = 1, perPage = 100) {
  return function (dispatch) {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        params.append(key, value);
      }
    });
    params.append('page', page);
    params.append('per_page', perPage);

    return apiV2
      .get(`/research/query?${params.toString()}`)
      .then(response => {
        return Promise.resolve(response.data);
      })
      .catch(error => {
        errorHandler(dispatch, error.response, API_ERROR);
        return Promise.reject(error);
      });
  };
}

/**
 * Trigger a CSV export job.
 */
export function exportResearchData(filters = {}) {
  return function (dispatch) {
    return apiV2
      .post('/research/export', filters)
      .then(response => {
        const job = response.data?.data || response.data;
        return Promise.resolve(job);
      })
      .catch(error => {
        errorHandler(dispatch, error.response, API_ERROR);
        return Promise.reject(error);
      });
  };
}

/**
 * Check export job status (for polling).
 */
export function checkExportJobStatus(jobId) {
  return function (dispatch) {
    return apiV2
      .get(`/research/export/${jobId}`)
      .then(response => {
        return Promise.resolve(response.data?.data || response.data);
      })
      .catch(error => {
        errorHandler(dispatch, error.response, API_ERROR);
        return Promise.resolve(null);
      });
  };
}

/**
 * List past export jobs.
 */
export function fetchExportJobs() {
  return function (dispatch) {
    return apiV2
      .get('/research/jobs')
      .then(response => {
        return Promise.resolve(response.data?.data || []);
      })
      .catch(error => {
        errorHandler(dispatch, error.response, API_ERROR);
        return Promise.resolve([]);
      });
  };
}

/**
 * Fetch chain of custody documentation for a job (or latest).
 */
export function fetchChainOfCustody(jobId) {
  return function (dispatch) {
    const url = jobId ? `/research/chain-of-custody/${jobId}` : '/research/chain-of-custody';
    return apiV2
      .get(url)
      .then(response => {
        return Promise.resolve(response.data?.data || response.data);
      })
      .catch(error => {
        errorHandler(dispatch, error.response, API_ERROR);
        return Promise.reject(error);
      });
  };
}

/**
 * Download a completed export.
 * Requests a short-lived download token (signed GCS URL), then triggers the
 * download via a programmatic <a> click. The signed URL has
 * Content-Disposition: attachment, so the browser downloads the file without
 * navigating away from the CMS.
 *
 * Returns a promise that resolves to { success: true } or { success: false }
 * so the caller can manage loading state.
 */
export function downloadResearchExport(jobId) {
  return function (dispatch) {
    return apiV2
      .post(`/research/export/${jobId}/download-token`)
      .then(response => {
        const downloadUrl = response.data?.data?.download_url || response.data?.download_url;
        if (!downloadUrl) {
          Swal.fire({ title: "Download failed", text: "No download URL returned from server.", icon: "error" });
          return { success: false };
        }
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        return { success: true };
      })
      .catch(error => {
        if (!error._sessionExpired) {
          Swal.fire({ title: "Download failed", text: error.response?.data?.message || "Could not generate download link.", icon: "error" });
        }
        return { success: false };
      });
  };
}
